// LeadTimelineSidebar.tsx
// Slide-in sidebar (right-hand panel) replacing the old timeline Dialog.
// Follows the same SHELL/INNER card chrome and G/C tokens as LeadDetailView.tsx.
// Bundles the timeline together with the Quick Share (barcode) card and the
// lead's contact details, so the user never has to close the sidebar to
// check something else.

import React from 'react';
import { X, Clock, IdCard, Phone, Mail, MapPin, Tag } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import {
  Lead,
  TimelineEvent,
  SHELL,
  INNER,
  Card,
  SectionHeader,
  InfoRow,
  TimelineItem,
  QuickShareCard,
  getBadgeStyle,
  getInitials,
  avc,
  SOURCE_ICONS,
} from '../LeadDetails';

interface LeadTimelineSidebarProps {
  visible: boolean;
  onHide: () => void;
  lead: Lead;
  events: TimelineEvent[];
}

const LeadTimelineSidebar: React.FC<LeadTimelineSidebarProps> = ({ visible, onHide, lead, events }) => {
  if (!visible) return null;

  const bs = getBadgeStyle(lead.status);
  const SourceIcon = SOURCE_ICONS[(lead.source || '').toLowerCase()] || Tag;
  const initials = getInitials(lead.name);
  const avatarColor = avc(lead.name || lead.email);

  return (
    <>
      <style>{`
        @keyframes ldv-sidebar-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onHide}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,22,0.35)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', zIndex: 1000 }}
      />

      {/* Sliding panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(620px, 100vw)',
          background: G.shellBg,
          borderLeft: `1px solid ${G.shellBorder}`,
          boxShadow: '-8px 0 30px rgba(17,85,80,0.18)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          animation: 'ldv-sidebar-slide-in 0.22s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            ...INNER,
            borderRadius: 0,
            borderTop: 'none',
            borderRight: 'none',
            borderLeft: 'none',
            padding: '13.8px 16.6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', minWidth: 0 }}>
            <div style={{ width: '35px', height: '35px', borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6.5px' }}>
                <span style={{ fontSize: '0.93rem', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</span>
                <span style={{ padding: '1.8px 7.4px', borderRadius: '20px', fontSize: '0.6rem', fontWeight: 700, textTransform: 'capitalize', background: bs.bg, color: bs.color, border: `1px solid ${bs.border}` }}>{lead.status || '—'}</span>
              </div>
              <span style={{ fontSize: '0.68rem', color: C.muted }}>{events.length} timeline events</span>
            </div>
          </div>
          <button
            onClick={onHide}
            style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${G.line}`, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '13.8px', display: 'flex', flexDirection: 'column', gap: '11px' }}>
          {/* <Card>
            <SectionHeader icon={IdCard} title="Contact Info" />
            <InfoRow icon={Phone} label="Phone" value={lead.phone} />
            <InfoRow icon={Mail} label="Email" value={lead.email} />
            <InfoRow icon={MapPin} label="City" value={lead.city} />
            <InfoRow icon={SourceIcon} label="Source" value={lead.source?.replace('_', ' ') || null} />
          </Card> */}

          {/* <QuickShareCard lead={lead} /> */}

          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16.6px 18.5px' }}>
              <SectionHeader icon={Clock} title="Timeline" count={events.length} />
              {events.map((ev, idx) => (
                <TimelineItem key={idx} ev={ev} isLast={idx === events.length - 1} lead={lead} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadTimelineSidebar;